// frontend/src/widgets/WorkflowStepBuilderWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-06 18:06 BST
//
// Description:
//   Business-first workflow task authoring widget.
//
// Change Log (GMT/BST):
//   2026-10-06 18:06 BST - Removed all dark-blue authoring surfaces and forced task/editor cards to the same
//                           white/neutral widget palette used by the surrounding Workflow Admin widgets.
//                           Retained visible per-task delete controls.
//   2026-10-06 17:54 BST - Restored standard Workflow Admin widget styling: white task/editor surfaces,
//                           dark text, neutral borders and orange selected/action accents. Removed
//                           the harsh dark-blue task/editor background introduced in the prior pass.
//   2026-10-06 17:58 BST - Removed UI seed control, restored visible per-task Delete action,
//                           and replaced hard-coded light control surfaces with Vision theme colours.
//   2026-10-06 17:44 BST - Added ATC Work Package seed entry point and replaced raw JSON-first
//                           authoring with business fields for Group, Requires Location, Evidence
//                           and Weekly Recurrence. Metadata is persisted in config_json; runtime
//                           enforcement remains explicitly deferred until its owning services are wired.
//   2026-10-06 17:18 BST - System/Approval clarification: System tasks are unassigned and
//                           complete only after successful Advanced Automation on step_ready;
//                           Approval tasks enter waiting state and require an approval decision.
//   2026-10-06 16:24 BST - Phase 1C.3 UX correction. Reframed normal authoring around
//                           Tasks rather than raw workflow-step persistence fields. New
//                           tasks default to Manual + Unassigned + Required. Stable keys,
//                           insertion position and engine defaults are derived automatically.
//                           Locked lifecycle milestones are rendered as protected markers.
//                           Advanced engine fields remain available without dominating the
//                           normal task-creation journey.
//   2026-09-10 09:05 BST - Mobile responsiveness pass; preserved controlled callbacks,
//                           reorder semantics and system-lock protection.
//
// Operational Notes:
//   - No API calls exist here; WorkflowPage owns mutations.
//   - workflow_opened/workflow_completed are backend-managed system milestones.
//   - Ordinary tasks are inserted immediately before workflow_completed.

import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";

const ASSIGNMENT_OPTIONS = [
  ["unassigned", "Unassigned"],
  ["inherit_job_primary", "Job primary resource"],
  ["inherit_job_co_resource", "Job co-resource"],
  ["inherit_all_job_resources", "All job resources"],
  ["inherit_previous_task_primary", "Previous task resource"],
  ["specific_resource", "Specific resource"],
  ["specific_role", "Specific role"],
  ["dispatcher_decides", "Dispatcher decides"],
];

const TASK_TYPES = [
  ["manual", "Manual Task"],
  ["approval", "Approval Task"],
  ["system", "System Task"],
];

const emptyForm = {
  title: "",
  description: "",
  task_type: "manual",
  assignment_mode: "unassigned",
  is_required: true,
  allow_skip: false,
  allow_override: false,
  activation_mode: "always",
  activation_rule_json: "",
  default_due_offset_minutes: "",
  default_duration_minutes: "",
  skill_warning_profile_id: "",
  display_group: "",
  requires_location: false,
  evidence_required: false,
  evidence_type: "image",
  weekly_recurrence: false,
  config_json: "",
};

const sameId = (a, b) => String(a ?? "") === String(b ?? "");
const isSystemStep = (step) => !!step?.is_system_locked || ["workflow_opened", "workflow_completed"].includes(String(step?.step_key || "").toLowerCase());

const slugify = (value) => {
  const base = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return base || "task";
};

const parseIntOrNull = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) throw new Error("Advanced numeric values must be whole numbers of zero or greater.");
  return n;
};

const parseJsonOrNull = (value, label) => {
  const text = String(value || "").trim();
  if (!text) return null;
  try { return JSON.parse(text); } catch { throw new Error(`${label} must contain valid JSON.`); }
};

const taskTypeFromStep = (step) => {
  if (step?.step_kind === "approval" || step?.completion_mode === "approval") return "approval";
  if (["notification", "backend_action"].includes(step?.step_kind) || ["automatic", "action_success", "system_event"].includes(step?.completion_mode)) return "system";
  return "manual";
};

const engineShapeForTaskType = (taskType) => {
  if (taskType === "approval") return { step_kind: "approval", completion_mode: "approval" };
  if (taskType === "system") return { step_kind: "backend_action", completion_mode: "action_success", assignment_mode: "unassigned" };
  return { step_kind: "task", completion_mode: "manual" };
};

const WorkflowStepBuilderWidget = ({
  selectedTemplate = null,
  selectedVersion = null,
  steps = [],
  selectedStepId = null,
  loading = {},
  onSelectStep,
  onCreateStep,
  onUpdateStep,
  onDeleteStep,
  onMoveStepUp,
  onMoveStepDown,
}) => {
  const { theme } = useTheme();
  const [form, setForm] = useState(emptyForm);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const orderedSteps = useMemo(() => [...(steps || [])].sort((a, b) => Number(a.sequence_no) - Number(b.sequence_no)), [steps]);
  const businessSteps = useMemo(() => orderedSteps.filter((s) => !isSystemStep(s)), [orderedSteps]);
  const selectedStep = useMemo(() => orderedSteps.find((s) => sameId(s.id, selectedStepId)) || null, [orderedSteps, selectedStepId]);
  const canEdit = selectedVersion?.status === "draft";

  useEffect(() => {
    if (!selectedStep || isSystemStep(selectedStep)) {
      setForm(emptyForm);
      setAdvancedOpen(false);
      return;
    }
    setForm({
      title: selectedStep.title || "",
      description: selectedStep.description || "",
      task_type: taskTypeFromStep(selectedStep),
      assignment_mode: selectedStep.assignment_mode || "unassigned",
      is_required: !!selectedStep.is_required,
      allow_skip: !!selectedStep.allow_skip,
      allow_override: !!selectedStep.allow_override,
      activation_mode: selectedStep.activation_mode || "always",
      activation_rule_json: selectedStep.activation_rule_json ? JSON.stringify(selectedStep.activation_rule_json, null, 2) : "",
      default_due_offset_minutes: selectedStep.default_due_offset_minutes ?? "",
      default_duration_minutes: selectedStep.default_duration_minutes ?? "",
      skill_warning_profile_id: selectedStep.skill_warning_profile_id ?? "",
      display_group: selectedStep.display_group || "",
      requires_location: !!selectedStep.config_json?.requires_location,
      evidence_required: !!selectedStep.config_json?.evidence?.required,
      evidence_type: selectedStep.config_json?.evidence?.type || "image",
      weekly_recurrence: !!selectedStep.config_json?.recurrence?.enabled && selectedStep.config_json?.recurrence?.cadence === "weekly",
      config_json: selectedStep.config_json && Object.keys(selectedStep.config_json).length ? JSON.stringify(selectedStep.config_json, null, 2) : "",
    });
  }, [selectedStep]);

  const startNew = () => {
    onSelectStep?.(null);
    setForm(emptyForm);
    setAdvancedOpen(false);
  };

  const buildPayload = () => {
    const title = String(form.title || "").trim();
    if (!title) throw new Error("Enter a task name.");
    const shape = engineShapeForTaskType(form.task_type);
    const existingKeys = new Set(orderedSteps.map((s) => String(s.step_key || "").toLowerCase()));
    let key = slugify(title);
    if (!selectedStep) {
      let suffix = 2;
      const base = key;
      while (existingKeys.has(key)) key = `${base}_${suffix++}`;
    }
    const completed = orderedSteps.find((s) => String(s.step_key || "").toLowerCase() === "workflow_completed");
    return {
      ...(selectedStep ? {} : { sequence_no: completed ? Number(completed.sequence_no) : undefined }),
      ...(selectedStep ? {} : { step_key: key }),
      title,
      description: String(form.description || "").trim() || null,
      step_kind: shape.step_kind,
      completion_mode: shape.completion_mode,
      activation_mode: form.activation_mode,
      activation_rule_json: parseJsonOrNull(form.activation_rule_json, "Activation rule"),
      is_required: !!form.is_required,
      is_system_locked: false,
      allow_skip: !!form.allow_skip,
      allow_override: !!form.allow_override,
      assignment_mode: shape.assignment_mode || form.assignment_mode,
      default_due_offset_minutes: parseIntOrNull(form.default_due_offset_minutes),
      default_duration_minutes: parseIntOrNull(form.default_duration_minutes),
      skill_warning_profile_id: parseIntOrNull(form.skill_warning_profile_id),
      is_hidden_in_progress_ui: false,
      display_group: String(form.display_group || "").trim() || null,
      config_json: (() => {
        const raw = parseJsonOrNull(form.config_json, "Configuration") || {};
        const next = { ...raw };
        if (form.requires_location) next.requires_location = true;
        else delete next.requires_location;
        if (form.evidence_required) next.evidence = { ...(next.evidence || {}), required: true, type: form.evidence_type || "image", minimum_count: next.evidence?.minimum_count || 1 };
        else delete next.evidence;
        if (form.weekly_recurrence) next.recurrence = { ...(next.recurrence || {}), enabled: true, cadence: "weekly" };
        else delete next.recurrence;
        return next;
      })(),
    };
  };

  const save = async () => {
    if (!canEdit) return alert("Select a draft version before editing tasks.");
    try {
      const payload = buildPayload();
      if (selectedStep) await onUpdateStep?.(selectedStep.id, payload);
      else await onCreateStep?.(payload);
    } catch (err) { alert(err?.message || "Failed to save task."); }
  };

  // Match the established Workflow Admin widgets: neutral white surfaces with
  // dark text, light borders and the configured action accent. Do not derive
  // editor/card surfaces from the application's dark navigation colour.
  const surface = "#ffffff";
  const surfaceAlt = "#f8fafc";
  const textColor = "#1f2937";
  const mutedColor = "#64748b";
  const borderColor = "#e5e7eb";
  const selectedBackground = "#fff7ed";
  const actionBackground = theme.button_action?.normal || theme.accent_color || "#f97316";
  const generalBackground = "#ffffff";
  const card = { border: `1px solid ${borderColor}`, borderRadius: 10, padding: 10, background: surface, color: textColor };
  const label = { fontSize: 12, fontWeight: 700, marginBottom: 4, color: textColor };
  const input = { width: "100%", minHeight: 40, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "8px 10px", boxSizing: "border-box", background: surface, color: textColor };
  const button = { minHeight: 40, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer", color: textColor, background: generalBackground };

  return (
    <div className="flex h-auto min-h-0 flex-col gap-3 md:h-full md:flex-row">
      <div className="flex min-h-0 flex-col gap-2 md:w-5/12">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div style={{ fontWeight: 700 }}>Tasks</div>
            <div style={{ fontSize: 12, color: mutedColor }}>Create the work people or the system must perform.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" style={{ ...button, background: actionBackground, color: "#ffffff", borderColor: actionBackground }} disabled={!canEdit} onClick={startNew}>+ Add Task</button>
          </div>
        </div>

        <div style={{ ...card, background: surfaceAlt, fontSize: 12, color: mutedColor }}>Workflow starts automatically</div>
        <div className="flex min-h-0 flex-col gap-2 overflow-y-auto">
          {businessSteps.map((step, index) => (
            <div key={step.id} style={{ ...card, background: sameId(step.id, selectedStepId) ? selectedBackground : surface, borderColor: sameId(step.id, selectedStepId) ? actionBackground : borderColor, borderWidth: sameId(step.id, selectedStepId) ? 2 : 1, cursor: "pointer" }} onClick={() => onSelectStep?.(step.id)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div style={{ fontWeight: 700 }}>{index + 1}. {step.title}</div>
                  <div style={{ fontSize: 12, color: mutedColor }}>{taskTypeFromStep(step) === "approval" ? "Approval" : taskTypeFromStep(step) === "system" ? "System" : "Manual"} · {step.assignment_mode || "unassigned"}{step.is_required ? " · Required" : ""}</div>
                </div>
                <div className="flex gap-1">
                  <button type="button" disabled={!canEdit || index === 0 || loading.reorderingStep} onClick={(e) => { e.stopPropagation(); onMoveStepUp?.(step, orderedSteps.indexOf(step)); }}>↑</button>
                  <button type="button" disabled={!canEdit || index === businessSteps.length - 1 || loading.reorderingStep} onClick={(e) => { e.stopPropagation(); onMoveStepDown?.(step, orderedSteps.indexOf(step)); }}>↓</button>
                  <button type="button" title="Delete task" aria-label={`Delete ${step.title}`} disabled={!canEdit || loading.deletingStep} onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete task \"${step.title}\"?`)) onDeleteStep?.(step.id); }} style={{ color: textColor, background: "#ffffff", border: `1px solid ${borderColor}`, borderRadius: 6, minWidth: 32 }}>×</button>
                </div>
              </div>
            </div>
          ))}
          {!loading.steps && businessSteps.length === 0 && <div style={{ ...card, color: mutedColor }}>No business tasks yet. Select <strong>Add Task</strong>.</div>}
        </div>
        <div style={{ ...card, background: surfaceAlt, fontSize: 12, color: mutedColor }}>Workflow completes automatically</div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto" style={card}>
        <div>
          <div style={{ fontWeight: 700 }}>{selectedStep && !isSystemStep(selectedStep) ? "Edit Task" : "Add Task"}</div>
          <div style={{ fontSize: 12, color: mutedColor }}>For a normal task, name + type + assignment is enough.</div>
        </div>

        <div><div style={label}>Task name</div><input style={input} value={form.title} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Deploy ATC" /></div>
        <div><div style={label}>Description</div><textarea style={{ ...input, minHeight: 72 }} value={form.description} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} /></div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><div style={label}>Type</div><select style={input} value={form.task_type} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, task_type: e.target.value }))}>{TASK_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          <div><div style={label}>Assigned to</div><select style={input} value={form.assignment_mode} disabled={!canEdit || form.task_type === "system"} onChange={(e) => setForm((p) => ({ ...p, assignment_mode: e.target.value }))}>{ASSIGNMENT_OPTIONS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
        </div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_required} disabled={!canEdit} onChange={(e) => setForm((p) => ({ ...p, is_required: e.target.checked }))} /> Required</label>

        {form.task_type === "system" && (
          <div style={{ ...card, background: surfaceAlt, fontSize: 12, color: mutedColor }}>
            System tasks are not assigned to a person. Add at least one <strong>Advanced Automation</strong> action for this task using the <strong>step_ready</strong> event. The task completes automatically only when all attempted actions succeed.
          </div>
        )}
        {form.task_type === "approval" && (
          <div style={{ ...card, background: surfaceAlt, fontSize: 12, color: mutedColor }}>
            Completing this task submits it for approval. It moves to <strong>Waiting</strong> until an authorised user approves or rejects it. Approval completes the task; rejection returns it to Ready.
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div><div style={label}>Group / Stage</div><input style={input} placeholder="e.g. Deploy ATC" value={form.display_group} onChange={(e) => setForm((p) => ({ ...p, display_group: e.target.value }))} /></div>
          <div className="flex flex-col gap-2" style={{ paddingTop: 22 }}>
            <label><input type="checkbox" checked={form.requires_location} onChange={(e) => setForm((p) => ({ ...p, requires_location: e.target.checked }))} /> Requires Location</label>
            <label><input type="checkbox" checked={form.evidence_required} onChange={(e) => setForm((p) => ({ ...p, evidence_required: e.target.checked }))} /> Requires Evidence</label>
            <label><input type="checkbox" checked={form.weekly_recurrence} onChange={(e) => setForm((p) => ({ ...p, weekly_recurrence: e.target.checked }))} /> Repeat Weekly</label>
          </div>
          {form.evidence_required && <div><div style={label}>Evidence Type</div><select style={input} value={form.evidence_type} onChange={(e) => setForm((p) => ({ ...p, evidence_type: e.target.value }))}><option value="image">Image</option><option value="file">File</option></select></div>}
        </div>

        <button type="button" style={{ ...button, background: generalBackground }} onClick={() => setAdvancedOpen((v) => !v)}>{advancedOpen ? "Hide advanced options" : "Advanced options"}</button>
        {advancedOpen && <div className="grid grid-cols-1 gap-3 md:grid-cols-2" style={{ paddingTop: 4 }}>
          <div><div style={label}>Activation</div><select style={input} value={form.activation_mode} onChange={(e) => setForm((p) => ({ ...p, activation_mode: e.target.value }))}><option value="always">Always</option><option value="conditional">Conditional</option></select></div>
          <div><div style={label}>Due offset (minutes)</div><input style={input} type="number" min="0" value={form.default_due_offset_minutes} onChange={(e) => setForm((p) => ({ ...p, default_due_offset_minutes: e.target.value }))} /></div>
          <div><div style={label}>Expected duration (minutes)</div><input style={input} type="number" min="0" value={form.default_duration_minutes} onChange={(e) => setForm((p) => ({ ...p, default_duration_minutes: e.target.value }))} /></div>
          <div><div style={label}>Skill warning profile ID</div><input style={input} type="number" min="0" value={form.skill_warning_profile_id} onChange={(e) => setForm((p) => ({ ...p, skill_warning_profile_id: e.target.value }))} /></div>
          <div className="flex flex-col gap-2"><label><input type="checkbox" checked={form.allow_skip} onChange={(e) => setForm((p) => ({ ...p, allow_skip: e.target.checked }))} /> Allow skip</label><label><input type="checkbox" checked={form.allow_override} onChange={(e) => setForm((p) => ({ ...p, allow_override: e.target.checked }))} /> Allow override</label></div>
          <div className="md:col-span-2"><div style={label}>Activation rule JSON</div><textarea style={{ ...input, minHeight: 80, fontFamily: "monospace" }} value={form.activation_rule_json} onChange={(e) => setForm((p) => ({ ...p, activation_rule_json: e.target.value }))} /></div>
          <div className="md:col-span-2"><div style={label}>Configuration JSON</div><textarea style={{ ...input, minHeight: 80, fontFamily: "monospace" }} value={form.config_json} onChange={(e) => setForm((p) => ({ ...p, config_json: e.target.value }))} /></div>
        </div>}

        <div className="flex flex-wrap gap-2">
          <button type="button" style={{ ...button, background: actionBackground, color: "#ffffff", borderColor: actionBackground }} disabled={!canEdit || loading.savingStep} onClick={save}>{loading.savingStep ? "Saving…" : selectedStep && !isSystemStep(selectedStep) ? "Save Task" : "Add Task"}</button>
          {selectedStep && !isSystemStep(selectedStep) && <button type="button" style={{ ...button, background: generalBackground }} disabled={!canEdit || loading.deletingStep} onClick={() => onDeleteStep?.(selectedStep.id)}>Delete Task</button>}
          {selectedStep && <button type="button" style={{ ...button, background: generalBackground }} onClick={startNew}>Clear</button>}
        </div>
      </div>
    </div>
  );
};

export default WorkflowStepBuilderWidget;
